import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import { useTranslation } from '@/components/utils/translations';
import { User } from '@/api/entities';
import { AccountFinal } from '@/api/entities';
import { PersonFinal } from '@/api/entities';
import { Button } from '@/components/ui/button';
import { apiFetch } from "@/utils/apiFetch";
import { NEOGTM_LOGO_PNG_URL } from '@/lib/brandAssets';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Home,
  TrendingUp,
  Building2,
  Users,
  Zap,
  Target,
  BookOpen,
  Settings,
  MessageCircle,
  Bell,
  LogOut,
  Menu,
  X,
  HelpCircle,
  Layers,
  Mail,
  BarChart3
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import NeoChatPanel from '../components/NeoChatPanel';
import FirstTimeUserSetup from '../components/FirstTimeUserSetup';
import OnboardingTour from '../components/OnboardingTour';
import { Toaster } from 'sonner';
import TenantSwitcher from '../components/admin/TenantSwitcher';
import Landing from '@/pages/Landing';
import { TooltipProvider } from '@/components/ui/tooltip';
import AlertBellTrigger from '../components/alerts/AlertBellTrigger';
import SessionTracker from '../components/SessionTracker';
import { resolveEffectiveTenant, canAccessDiambraTenant, canAccessEvasysTenant, canAccessLearnwiseTenant, canAccessCalmedicaTenant, canAccessDemarqueTenant, canAccessBibliothecaTenant, canAccessNiryoTenant, canAccessFunidataTenant, canAccessAkariTenant, canAccessAlmentorTenant, canAccessGravytyTenant, canAccessInfoneticaTenant, canAccessCayuseTenant, canAccessUplannerTenant, canAccessEdusignTenant, canAccessLiaisonTenant, canAccessSebaTenant, canAccessRumiTenant, canAccessStudentcrmTenant, canAccessUnit4Tenant, canAccessBlackboardTenant, canAccessConstructorTenant, canAccessEfektaTenant, canAccessJpikTenant, canAccessTestweTenant, canAccessBlackbullionTenant, canAccessWooclapTenant, canAccessPolleverywhereTenant, canAccessPositivoTenant, canAccessFeedbackfruitsTenant, canAccessLegacyFeedbackfruitsTenant, canAccessLegacyDemarqueTenant, canAccessFullfabricTenant, canAccessLegacyFullfabricTenant, canAccessSurpassTenant, canAccessLegacySurpassTenant, isDemarqueEmail, isPlatformAccessBlockedEmail, isEvasysFamilyTenant, isFeedbackfruitsFamilyTenant, isDemarqueFamilyTenant, isBibliothecaFamilyTenant, isFullfabricFamilyTenant, isSurpassFamilyTenant, isLegacyFeedbackfruitsTenant, isLegacyDemarqueTenant, isLegacyFullfabricTenant, isLegacySurpassTenant, isFeedbackfruitsEmail, isFullfabricEmail, isSurpassEmail } from '@/lib/tenantUtils';
import {
  UserContext,
  CurrentTenantContext,
  SessionTenantContext,
  useUser,
  useCurrentTenant
} from '@/contexts/UserContext';
import {
  deriveCapabilities,
  isPathAllowedForCapabilities,
  resolvePostAuthRoute,
} from '@/lib/tenantCapabilities';
import { ALLOWED_UI_LANGUAGE_CODES, resolvePlatformUiLanguage } from '@/components/utils/translations';
import { fetchChatbotBriefCountOnly } from '@/lib/chatbotBrief';
import { fetchChatbotTriggersCountOnly } from '@/lib/chatbotTriggers';
import { dispatchAdminTenantChange, handleChatbotTenantSwitch } from '@/lib/chatbotCache';
import { ChatSessionContext } from '@/contexts/ChatSessionContext';

export { useUser, useCurrentTenant };

const FETCH_TENANT_HEADER_PATCH_FLAG = '__NEO_FETCH_TENANT_HEADER_PATCHED__';

function getLiveTenantHeaderValue() {
  const role = localStorage.getItem('user-role');
  const adminActiveTenant = role === 'admin' ? localStorage.getItem('admin-active-tenant') : null;
  const sessionTenant =
    typeof window !== 'undefined' ? window.__NEO_SESSION_TENANT_ID : null;
  const fallbackTenant = localStorage.getItem('user-tenant-id');
  const resolved = resolveEffectiveTenant(
    adminActiveTenant,
    sessionTenant,
    { tenant_id: fallbackTenant, role },
    'instructure'
  );
  return resolved || 'instructure';
}

function shouldAttachTenantHeader(urlValue) {
  if (!urlValue) return true;
  const urlString = String(urlValue);
  return (
    urlString.startsWith('/api/') ||
    urlString.includes('://api.neogtm.com/') ||
    urlString.includes('://localhost:3000/')
  );
}

function patchFetchWithLiveTenantHeader() {
  if (typeof window === 'undefined') return;
  if (window[FETCH_TENANT_HEADER_PATCH_FLAG]) return;

  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input, init = {}) => {
    const requestUrl = typeof input === 'string' ? input : input?.url;
    if (!shouldAttachTenantHeader(requestUrl)) {
      return originalFetch(input, init);
    }

    const role = localStorage.getItem('user-role');
    if (role !== 'admin') {
      return originalFetch(input, init);
    }

    const tenantId = getLiveTenantHeaderValue();
    const headers = new Headers(init?.headers || (input instanceof Request ? input.headers : undefined));
    headers.set('X-Tenant-ID', tenantId);

    const nextInit = { ...init, headers };
    // Chrome/DevTools often attributes failed requests to this line — it is only the global
    // fetch wrapper (admin X-Tenant-ID). The real caller is elsewhere (e.g. PersonProfile, OutreachComposer).
    return originalFetch(input, nextInit);
  };

  window[FETCH_TENANT_HEADER_PATCH_FLAG] = true;
}

// One-time cleanup: Clear old cache entries without tenant_id
function cleanupOldCacheEntries() {
  try {
    // Clear old Accounts cache format
    const oldAccountsKey = 'accounts-cache-v2';
    if (sessionStorage.getItem(oldAccountsKey)) {
      console.log('🧹 Layout: Clearing old accounts cache format');
      sessionStorage.removeItem(oldAccountsKey);
    }
    
    // Clear old AccountFinal cache entries (without tenant_id in key)
    // Pattern: account-final-{accountId} (old) vs account-final-{tenantId}-{accountId} (new)
    const keysToRemove = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith('account-final-')) {
        // Old format has exactly 3 parts: ['account', 'final', '{accountId}']
        // New format has 4 parts: ['account', 'final', '{tenantId}', '{accountId}']
        const parts = key.split('-');
        if (parts.length === 3 && parts[0] === 'account' && parts[1] === 'final') {
          keysToRemove.push(key);
        }
      }
    }
    if (keysToRemove.length > 0) {
      console.log('🧹 Layout: Clearing', keysToRemove.length, 'old account-final cache entries');
      keysToRemove.forEach(k => sessionStorage.removeItem(k));
    }
  } catch (e) {
    console.warn('Failed to clean old cache:', e);
  }
}

// Normalize display_name from various possible field names (falls back to full_name only when display_name is empty)
function normalizeDisplayName(u) {
  if (!u) return u;
  const display =
    u.display_name ||
    u.displayName ||
    u.name ||
    u.full_name ||
    '';

  return { ...u, display_name: display };
}

function ProductRouteGuard({ children, capabilities, isLoading }) {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (isLoading) return;
    const path = location.pathname;
    if (!isPathAllowedForCapabilities(path, capabilities)) {
      navigate(resolvePostAuthRoute(capabilities), { replace: true });
    }
  }, [location.pathname, capabilities, isLoading, navigate]);

  return children;
}

export default function Layout({ children, currentPageName }) {
  const { t } = useTranslation();
  const [user, setUser] = useState(null);
  const [sessionTenant, setSessionTenant] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showFirstTimeSetup, setShowFirstTimeSetup] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showChat, setShowChat] = useState(false);
  const [briefBadge, setBriefBadge] = useState(false);

  // One-time cleanup of old cache entries on mount
  useEffect(() => {
    cleanupOldCacheEntries();
  }, []); // Run once on mount
  const [chatContext, setChatContext] = useState(null);
  const [pageAccount, setPageAccount] = useState(null);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [activeTenant, setActiveTenant] = useState(() => {
    try {
      const stored = localStorage.getItem('user-tenant-id');
      if (stored && stored !== 'default') return stored;
    } catch {
      // ignore
    }
    return 'instructure';
  });
  const [isLoggingOut, setIsLoggingOut] = useState(false);  // Add this line
  const isLoggingOutRef = useRef(false);  // Add this line
  const loadUserAbortRef = useRef(null);  // Add this line
  const location = useLocation();

  useEffect(() => {
    patchFetchWithLiveTenantHeader();
  }, []);
  
  const capabilities = deriveCapabilities(sessionTenant);

  const allNavigationItems = [
    { name: t('home'), href: '/Home', icon: Home, requiresCore: true },
    { name: t('market'), href: '/Market', icon: TrendingUp, requiresCore: true },
    { name: t('accounts'), href: '/Accounts', icon: Building2, requiresCore: true },
    { name: t('people'), href: '/People', icon: Users, requiresCore: true },
    { name: t('signals'), href: '/Signals', icon: Zap, requiresCore: true },
    { name: t('procurement'), href: '/Procurement', icon: Target, requiresProcure: true },
    { name: t('playbooks'), href: '/Playbooks', icon: BookOpen, requiresCore: true },
  ];

  const navigationItems = allNavigationItems.filter((item) => {
    if (item.requiresCore && !capabilities.showCore) return false;
    if (item.requiresProcure && !capabilities.showProcure) return false;
    return true;
  });

  const logoHref = resolvePostAuthRoute(capabilities);

  // Check if we're on public pages (no auth required)
  const isPublicPage = location.pathname === '/' || location.pathname === '/Landing' || location.pathname === '/Auth' || location.pathname === '/AuthCallback' || location.pathname === '/ForgotPassword' || location.pathname.startsWith('/ResetPassword');

  useEffect(() => {
    if (!activeTenant) return;
    window.__NEO_SESSION_TENANT_ID = activeTenant;
    if (user?.role !== 'admin') {
      localStorage.setItem('user-tenant-id', activeTenant);
    }
  }, [activeTenant, user?.role]);

  useEffect(() => {
    // Skip authentication check on public pages or during logout
    if (isPublicPage || isLoggingOut) {
      setIsLoading(false);
      return;
    }
  
    // Abort any previous in-flight loadUser
    if (loadUserAbortRef.current) {
      try { loadUserAbortRef.current.abort(); } catch {}
    }
    const controller = new AbortController();
    loadUserAbortRef.current = controller;
  
    loadUser(controller.signal);
  
    return () => {
      try { controller.abort(); } catch {}
    };
  }, [location.pathname, isPublicPage, isLoggingOut]);

  const loadUser = async (signal) => {
    if (isLoggingOutRef.current || signal?.aborted) return;
  
    try {
      const API_BASE_URL =
        window.location.hostname === 'localhost'
          ? 'https://api.neogtm.com'
          : 'https://api.neogtm.com';
  
      if (isLoggingOutRef.current || signal?.aborted) return;
  
      let currentUser = null;
      let tenantFromSession = null;
  
      // Try custom auth session first
      try {
        if (isLoggingOutRef.current || signal?.aborted) return;
  
        const sessionResponse = await fetch(`${API_BASE_URL}/api/auth/session`, {
          credentials: 'include',
          signal,
        });
  
        if (isLoggingOutRef.current || signal?.aborted) return;
  
        if (sessionResponse.ok) {
          const sessionData = await sessionResponse.json();
          if (sessionData.tenant) {
            tenantFromSession = sessionData.tenant;
          }
          if (sessionData.user) {
            currentUser = normalizeDisplayName(sessionData.user);

            if (!currentUser.id) {
              try {
                const base44User = await User.me();
                currentUser = {
                  ...currentUser,
                  id: currentUser.id || base44User?.id,
                  email: currentUser.email || base44User?.email,
                  full_name: currentUser.full_name || base44User?.full_name,
                };
              } catch (e) {
                if (!window.__layoutIdentityEnrichFailLogged) {
                  window.__layoutIdentityEnrichFailLogged = true;
                  console.warn('Layout: could not enrich custom-auth user with identity fields');
                }
              }
            }

            console.log('Layout: Loaded user from custom auth:', {
              email: currentUser.email,
              role: currentUser.role,
              tenant_id: currentUser.tenant_id,
              isAuthenticated: true
            });
          }
        }
      } catch (e) {
        if (e?.name === 'AbortError') return;
        console.log('Layout: Custom auth session check failed, trying Base44');
      }
  
      // Fall back to Base44 for existing users
      if (!currentUser) {
        if (isLoggingOutRef.current || signal?.aborted) return;
        try {
          currentUser = await User.me();
        } catch (e) {
          if (e?.name === 'AbortError') return;
          if (!window.__layoutUserMeFailLogged) {
            window.__layoutUserMeFailLogged = true;
            console.warn('Layout: Could not load user identity (User.me failed), using session only');
          }
          setUser(null);
          setIsAuthenticated(false);
          setIsLoading(false);
          return;
        }
        if (isLoggingOutRef.current || signal?.aborted) return;
        currentUser = normalizeDisplayName(currentUser);
        console.log('Layout: Loaded user from Base44:', {
          email: currentUser.email,
          role: currentUser.role,
          tenant_id: currentUser.tenant_id,
          isAuthenticated: true
        });
      }

      if (isPlatformAccessBlockedEmail(currentUser?.email)) {
        setUser(null);
        setIsAuthenticated(false);
        setIsLoading(false);
        try {
          await fetch(`${API_BASE_URL}/api/auth/logout`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
          });
        } catch (_) {
          // no-op
        }
        const blockedParam = isDemarqueEmail(currentUser?.email) ? 'demarque' : 'smowltech';
        window.location.href = `/Auth?blocked=${blockedParam}`;
        return;
      }

      // Fetch profile data from Supabase (including photo) - use explicit admin-gated tenant resolution
      try {
        if (isLoggingOutRef.current || signal?.aborted) return;

        if (!currentUser?.id) {
          if (!window.__layoutMissingIdLogged) {
            window.__layoutMissingIdLogged = true;
            console.warn('Layout: currentUser.id is missing, skipping tenant profile fetch');
          }
          currentUser.display_name = '';
          currentUser.profile_photo_url = '';
          currentUser.job_title = '';
          currentUser.company_name = '';
          currentUser = normalizeDisplayName(currentUser);
        } else {
          const adminTenant =
            currentUser?.role === 'admin'
              ? localStorage.getItem('admin-active-tenant')
              : null;
          const profileTenantId = currentUser?.role === 'admin'
            ? resolveEffectiveTenant(adminTenant, activeTenant, currentUser, 'instructure')
            : (currentUser?.tenant_id || 'instructure');

          if (!window.__layoutProfileFetchLogged) {
            window.__layoutProfileFetchLogged = true;
            console.log('Layout: profile fetch', {
              userId: currentUser.id,
              email: currentUser.email,
              role: currentUser.role,
              profileTenantId,
            });
          }

          const supabaseResponse = await fetch(`${API_BASE_URL}/api/user/me`, {
            headers: {
              'x-user-id': currentUser.id,
              'x-tenant-id': profileTenantId
            },
            credentials: 'include',
            signal,
          });

          if (isLoggingOutRef.current || signal?.aborted) return;

          if (supabaseResponse.ok) {
            const supabaseUser = await supabaseResponse.json();
            // Only use tenant-scoped fields from API; never from Base44 (avoids cross-tenant leakage)
            currentUser.display_name = supabaseUser.display_name || '';
            currentUser.profile_photo_url = supabaseUser.profile_photo_url || '';
            currentUser.job_title = supabaseUser.job_title || '';
            currentUser.company_name = supabaseUser.company_name || '';
            currentUser = normalizeDisplayName(currentUser);
          } else {
            currentUser.display_name = '';
            currentUser.profile_photo_url = '';
            currentUser.job_title = '';
            currentUser.company_name = '';
            currentUser = normalizeDisplayName(currentUser);
          }
        }
      } catch (e) {
        if (e?.name === 'AbortError') return;
        if (!window.__layoutProfileFailLogged) {
          window.__layoutProfileFailLogged = true;
          console.warn('Layout: Could not fetch tenant profile, using identity only');
        }
        currentUser.display_name = '';
        currentUser.profile_photo_url = '';
        currentUser.job_title = '';
        currentUser.company_name = '';
        currentUser = normalizeDisplayName(currentUser);
      }
  
      if (isLoggingOutRef.current || signal?.aborted) return;
  
      if (currentUser?.id) localStorage.setItem('user-id', currentUser.id);
      if (currentUser?.role) localStorage.setItem('user-role', currentUser.role);

      // Resolve/sync tenant before marking auth ready, so first authed calls get correct tenant.
      if (currentUser.role === 'admin') {
        const storedTenant = localStorage.getItem('admin-active-tenant');
        const effectiveTenant = resolveEffectiveTenant(storedTenant, null, currentUser, currentUser.tenant_id || 'instructure');
        if (storedTenant === 'diambra' && !canAccessDiambraTenant(currentUser)) {
          localStorage.removeItem('admin-active-tenant');
        }
        if (isEvasysFamilyTenant(storedTenant) && !canAccessEvasysTenant(currentUser)) {
          localStorage.removeItem('admin-active-tenant');
        }
        if (storedTenant === 'learnwise' && !canAccessLearnwiseTenant(currentUser)) {
          localStorage.removeItem('admin-active-tenant');
        }
        if (storedTenant === 'calmedica' && !canAccessCalmedicaTenant(currentUser)) {
          localStorage.removeItem('admin-active-tenant');
        }
        if (isDemarqueFamilyTenant(storedTenant) && !canAccessDemarqueTenant(currentUser)) {
          localStorage.removeItem('admin-active-tenant');
        }
        if (isBibliothecaFamilyTenant(storedTenant) && !canAccessBibliothecaTenant(currentUser)) {
          localStorage.removeItem('admin-active-tenant');
        }
        if (storedTenant === 'niryo' && !canAccessNiryoTenant(currentUser)) {
          localStorage.removeItem('admin-active-tenant');
        }
        if (isLegacyDemarqueTenant(storedTenant) && !canAccessLegacyDemarqueTenant(currentUser)) {
          localStorage.removeItem('admin-active-tenant');
          if (isDemarqueEmail(currentUser?.email)) {
            localStorage.setItem('admin-active-tenant', 'demarque_V2');
          }
        }
        if (storedTenant === 'funidata' && !canAccessFunidataTenant(currentUser)) {
          localStorage.removeItem('admin-active-tenant');
        }
        if (storedTenant === 'akari' && !canAccessAkariTenant(currentUser)) {
          localStorage.removeItem('admin-active-tenant');
        }
        if (storedTenant === 'almentor' && !canAccessAlmentorTenant(currentUser)) {
          localStorage.removeItem('admin-active-tenant');
        }
        if (storedTenant === 'gravyty' && !canAccessGravytyTenant(currentUser)) {
          localStorage.removeItem('admin-active-tenant');
        }
        if (storedTenant === 'infonetica' && !canAccessInfoneticaTenant(currentUser)) {
          localStorage.removeItem('admin-active-tenant');
        }
        if (storedTenant === 'cayuse' && !canAccessCayuseTenant(currentUser)) {
          localStorage.removeItem('admin-active-tenant');
        }
        if (storedTenant === 'uplanner' && !canAccessUplannerTenant(currentUser)) {
          localStorage.removeItem('admin-active-tenant');
        }
        if (storedTenant === 'edusign' && !canAccessEdusignTenant(currentUser)) {
          localStorage.removeItem('admin-active-tenant');
        }
        if (storedTenant === 'liaison' && !canAccessLiaisonTenant(currentUser)) {
          localStorage.removeItem('admin-active-tenant');
        }
        if (storedTenant === 'seba' && !canAccessSebaTenant(currentUser)) {
          localStorage.removeItem('admin-active-tenant');
        }
        if (storedTenant === 'rumi' && !canAccessRumiTenant(currentUser)) {
          localStorage.removeItem('admin-active-tenant');
        }
        if (storedTenant === 'studentcrm' && !canAccessStudentcrmTenant(currentUser)) {
          localStorage.removeItem('admin-active-tenant');
        }
        if (storedTenant === 'unit4' && !canAccessUnit4Tenant(currentUser)) {
          localStorage.removeItem('admin-active-tenant');
        }
        if (storedTenant === 'blackboard' && !canAccessBlackboardTenant(currentUser)) {
          localStorage.removeItem('admin-active-tenant');
        }
        if ((storedTenant === 'constructor' || storedTenant === 'constructor_tech') && !canAccessConstructorTenant(currentUser)) {
          localStorage.removeItem('admin-active-tenant');
        }
        if (storedTenant === 'efekta' && !canAccessEfektaTenant(currentUser)) {
          localStorage.removeItem('admin-active-tenant');
        }
        if (storedTenant === 'testwe' && !canAccessTestweTenant(currentUser)) {
          localStorage.removeItem('admin-active-tenant');
        }
        if (storedTenant === 'blackbullion' && !canAccessBlackbullionTenant(currentUser)) {
          localStorage.removeItem('admin-active-tenant');
        }
        if (storedTenant === 'wooclap' && !canAccessWooclapTenant(currentUser)) {
          localStorage.removeItem('admin-active-tenant');
        }
        if (storedTenant === 'polleverywhere' && !canAccessPolleverywhereTenant(currentUser)) {
          localStorage.removeItem('admin-active-tenant');
        }
        if (storedTenant === 'positivo' && !canAccessPositivoTenant(currentUser)) {
          localStorage.removeItem('admin-active-tenant');
        }
        if (storedTenant === 'jpik' && !canAccessJpikTenant(currentUser)) {
          localStorage.removeItem('admin-active-tenant');
        }
        if (isFeedbackfruitsFamilyTenant(storedTenant) && !canAccessFeedbackfruitsTenant(currentUser)) {
          localStorage.removeItem('admin-active-tenant');
        }
        if (isLegacyFeedbackfruitsTenant(storedTenant) && !canAccessLegacyFeedbackfruitsTenant(currentUser)) {
          localStorage.removeItem('admin-active-tenant');
          if (isFeedbackfruitsEmail(currentUser?.email)) {
            localStorage.setItem('admin-active-tenant', 'feedbackfruits_V2');
          }
        }
        if (isFullfabricFamilyTenant(storedTenant) && !canAccessFullfabricTenant(currentUser)) {
          localStorage.removeItem('admin-active-tenant');
        }
        if (isLegacyFullfabricTenant(storedTenant) && !canAccessLegacyFullfabricTenant(currentUser)) {
          localStorage.removeItem('admin-active-tenant');
          if (isFullfabricEmail(currentUser?.email)) {
            localStorage.setItem('admin-active-tenant', 'fullfabric_V2');
          }
        }
        if (isSurpassFamilyTenant(storedTenant) && !canAccessSurpassTenant(currentUser)) {
          localStorage.removeItem('admin-active-tenant');
        }
        if (isLegacySurpassTenant(storedTenant) && !canAccessLegacySurpassTenant(currentUser)) {
          localStorage.removeItem('admin-active-tenant');
          if (isSurpassEmail(currentUser?.email)) {
            localStorage.setItem('admin-active-tenant', 'surpass_V2');
          }
        }
        console.log('Layout: Admin user, stored tenant:', storedTenant, 'effective:', effectiveTenant);
        localStorage.setItem('user-tenant-id', effectiveTenant);
        window.__NEO_SESSION_TENANT_ID = effectiveTenant;
        setActiveTenant(effectiveTenant);
      } else {
        const sessionTenant = currentUser.tenant_id && currentUser.tenant_id !== 'default'
          ? currentUser.tenant_id
          : null;
        const effective = resolveEffectiveTenant(null, sessionTenant, currentUser, 'instructure');
        localStorage.setItem('user-tenant-id', effective);
        window.__NEO_SESSION_TENANT_ID = effective;
        setActiveTenant(effective);
      }

      // Normalize display name one final time before checking
      currentUser = normalizeDisplayName(currentUser);
      if (typeof window !== 'undefined') {
        const storedUiLang = localStorage.getItem('ui-language');
        if (storedUiLang && ALLOWED_UI_LANGUAGE_CODES.has(storedUiLang)) {
          currentUser = { ...currentUser, ui_language: storedUiLang };
        }
      }
      setSessionTenant(tenantFromSession);
      setUser(currentUser);
      setIsAuthenticated(true);

      if (currentUser && !currentUser.display_name) {
        setShowFirstTimeSetup(true);
      }

      // Per-user keys for onboarding
      const userKey = currentUser?.id || currentUser?.email || 'anon';
      const COMPLETED_KEY = `neo-onboarding-completed:${userKey}`;
      const NEW_SIGNUP_KEY = `new-signup:${userKey}`;
      const LEGACY_COMPLETED_KEY = 'neo-onboarding-completed';
      const LEGACY_NEW_SIGNUP_KEY = 'new-signup';

      // One-time migration: if legacy key exists, migrate to per-user key
      if (localStorage.getItem(LEGACY_COMPLETED_KEY) === 'true' && userKey !== 'anon') {
        localStorage.setItem(COMPLETED_KEY, 'true');
        localStorage.removeItem(LEGACY_COMPLETED_KEY);
      }
      if (localStorage.getItem(LEGACY_NEW_SIGNUP_KEY) === 'true' && userKey !== 'anon') {
        localStorage.setItem(NEW_SIGNUP_KEY, 'true');
        localStorage.removeItem(LEGACY_NEW_SIGNUP_KEY);
      }

      // Helper to read query params (handles both BrowserRouter and HashRouter)
      function readQueryParam(name) {
        const p1 = new URLSearchParams(window.location.search || '');
        if (p1.has(name)) return p1.get(name);

        const hash = window.location.hash || '';
        const q = hash.indexOf('?');
        if (q >= 0) {
          const p2 = new URLSearchParams(hash.slice(q));
          if (p2.has(name)) return p2.get(name);
        }
        return null;
      }

      // Check for new_signup in URL params
      if (readQueryParam('new_signup') === '1') {
        localStorage.setItem(NEW_SIGNUP_KEY, 'true');
        const params = new URLSearchParams(window.location.search);
        params.delete('new_signup');
        const newUrl = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}${window.location.hash || ''}`;
        window.history.replaceState({}, '', newUrl);
      }

      const onboardingCompleted =
        Boolean(currentUser?.onboarding_completed) ||
        localStorage.getItem(COMPLETED_KEY) === 'true';

      // Check if this is a new signup (indicated by localStorage flag or URL param)
      const params = new URLSearchParams(window.location.search);
      if (params.get('new_signup') === '1') {
        localStorage.setItem(NEW_SIGNUP_KEY, 'true');
        // Clean URL
        params.delete('new_signup');
        const newUrl = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}${window.location.hash || ''}`;
        window.history.replaceState({}, '', newUrl);
      }
      
      const isNewSignup = localStorage.getItem(NEW_SIGNUP_KEY) === 'true' || 
                         localStorage.getItem(LEGACY_NEW_SIGNUP_KEY) === 'true';

      // Check if user was recently created (within last 5 minutes as fallback)
      const createdAtMs = currentUser?.created_at ? Date.parse(currentUser.created_at) : NaN;
      const isRecentlyCreated = Number.isFinite(createdAtMs) && createdAtMs > (Date.now() - 5 * 60 * 1000);

      const isHome = location.pathname === '/Home';

      // Only show onboarding for new signups on their first visit to Home
      const caps = deriveCapabilities(tenantFromSession);
      const shouldShowOnboarding =
        caps.showCore &&
        currentUser &&
        currentUser.display_name &&
        !onboardingCompleted &&
        (isNewSignup || isRecentlyCreated) &&
        isHome;

      if (shouldShowOnboarding && !showOnboarding) {
        requestAnimationFrame(() => {
          setShowOnboarding(true);
        });
      }
    } catch (error) {
      console.log('Layout: User not authenticated', error);
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!isAuthenticated || !user) return;

    const preloadChatData = async () => {
      try {
        const effectiveTenant = activeTenant || user.tenant_id || 'instructure';
        
        // DISABLE cache check - always load fresh for now
        console.log('🔄 Layout: Skipping cache, will load on chat open for tenant:', effectiveTenant);
        
        // DON'T preload - let the chat panel handle it when it opens
        return;
        
      } catch (error) {
        console.error('❌ Error in Layout preload (now disabled):', error);
      }
    };

    // Don't run preload anymore
    // const timer = setTimeout(preloadChatData, 1000);
    // return () => clearTimeout(timer);
  }, [isAuthenticated, user, activeTenant]);


  const handleAdminTenantChange = useCallback(
    (newTenantId) => {
      handleChatbotTenantSwitch({
        previousTenantId: activeTenant,
        nextTenantId: newTenantId,
        userId: user?.id,
      });
      localStorage.setItem('admin-active-tenant', newTenantId);
      localStorage.setItem('user-tenant-id', newTenantId);
      setActiveTenant(newTenantId);
      // Drop account anchor from chat context so account_id cannot lag behind tenant_id.
      setPageAccount(null);
      setChatContext((prev) => {
        if (!prev) return prev;
        const {
          account_id: _a,
          accountId: _b,
          preselectedAccount: _c,
          ...rest
        } = prev;
        return { ...rest, tenant_id: newTenantId };
      });
      dispatchAdminTenantChange();
    },
    [activeTenant, user?.id],
  );

  const refreshBriefBadge = useCallback(async () => {
    if (!capabilities.showCore || !user?.id || showChat) return;
    const adminTenant =
      user?.role === 'admin' ? localStorage.getItem('admin-active-tenant') : null;
    const tenantId = resolveEffectiveTenant(adminTenant, activeTenant, user, 'instructure');
    const language = resolvePlatformUiLanguage(user, tenantId);
    try {
      const [brief, triggers] = await Promise.all([
        fetchChatbotBriefCountOnly(tenantId, user.id, { language }),
        fetchChatbotTriggersCountOnly(tenantId, user.id, { language }),
      ]);
      setBriefBadge(
        (Number(brief?.unviewed_count) || 0) > 0 ||
          (Number(triggers?.unviewed_count) || 0) > 0,
      );
    } catch {
      setBriefBadge(false);
    }
  }, [capabilities.showCore, user, activeTenant, showChat]);

  useEffect(() => {
    if (!isAuthenticated || !user?.id || !capabilities.showCore) {
      setBriefBadge(false);
      return;
    }
    refreshBriefBadge();
    const interval = setInterval(refreshBriefBadge, 90000);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') refreshBriefBadge();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [isAuthenticated, user?.id, capabilities.showCore, refreshBriefBadge]);

  const toggleChat = () => {
    setShowChat((prev) => {
      if (!prev) setBriefBadge(false);
      return !prev;
    });
  };

  const handleFirstTimeSetupComplete = (displayName) => {
    setShowFirstTimeSetup(false);
    setUser(prev => ({ ...prev, display_name: displayName }));
    setTimeout(() => setShowOnboarding(true), 500);
  };

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
  };

  const handleQuickAction = (action) => {
    setShowOnboarding(false);
    // If action is "Meeting Prep", trigger the meeting prep flow in the chat
    if (action === "Meeting Prep") {
      startChatSession({ 
        prefilledPrompt: "Prepare me for a meeting",
        triggerMeetingPrep: true 
      });
    } else {
      startChatSession({ prefilledPrompt: action });
    }
  };

  const handleLogout = async () => {
    // Abort in-flight loadUser first
    try { loadUserAbortRef.current?.abort(); } catch {}
  
    // Set global flag for other components
    window.__NEO_LOGGING_OUT__ = true;
  
    isLoggingOutRef.current = true;
    setIsLoggingOut(true);
    setIsAuthenticated(false);
    setUser(null);
    setSessionTenant(null);
  
    localStorage.clear();
    sessionStorage.clear();
  
    const API_BASE_URL = window.location.hostname === 'localhost'
      ? 'https://api.neogtm.com'
      : 'https://api.neogtm.com';
  
    // Await logout so server can clear session cookie before redirect.
    // Safari needs this: redirecting too soon leaves the cookie intact, so user appears still logged in.
    try {
      await Promise.race([
        fetch(`${API_BASE_URL}/api/auth/logout`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        }),
        new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 3000))
      ]);
    } catch (_) {
      // Proceed with redirect even if logout API fails (e.g. offline, Safari cookie issues)
    }
  
    // Use href with cache-busting to force full reload; Safari bfcache can restore old state with replace()
    window.location.href = `/Auth?logout=${Date.now()}`;
  };

  const startChatSession = (context) => {
    const adminTenant =
      user?.role === 'admin'
        ? localStorage.getItem('admin-active-tenant')
        : null;
    const tenantId = resolveEffectiveTenant(adminTenant, activeTenant, user, 'instructure');
    const enrichedContext = { ...context, tenant_id: tenantId };
    console.log('🚀 Layout: Starting chat with tenant:', tenantId, {
      adminTenant,
      currentTenant: activeTenant,
      userTenantId: user?.tenant_id
    });
    setChatContext(enrichedContext);
    setBriefBadge(false);
    setShowChat(true);
  };

  const registerPageAccount = useCallback((account) => {
    if (!account) {
      setPageAccount(null);
      return;
    }
    const account_id = String(account.account_id || '').trim();
    if (!account_id) {
      setPageAccount(null);
      return;
    }
    setPageAccount({
      account_id,
      account_name: account.account_name || '',
      id: account.id,
    });
  }, []);

  const childrenWithProps = children
    ? React.cloneElement(children, {
        startChatSession,
        currentTenant: activeTenant
      })
    : null;

  // If we're on public pages, render children directly without auth check
  if (isPublicPage) {
    return <>{children}</>;
  }

  // If logging out, show nothing (prevents any child components from making API calls)
  if (isLoggingOut || isLoggingOutRef.current) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Landing />;
  }

  return (
    <TooltipProvider delayDuration={150}>
      <UserContext.Provider value={user}>
        <SessionTenantContext.Provider value={sessionTenant}>
        <CurrentTenantContext.Provider value={activeTenant}>
          <ChatSessionContext.Provider value={{ startChatSession, registerPageAccount, currentTenant: activeTenant }}>
          <SessionTracker user={user} tenantId={activeTenant} />
          <div className={`min-h-screen bg-gray-50 overflow-x-hidden ${capabilities.showCore ? 'pb-16 md:pb-0' : ''}`}>
        <style>{`
          .neo-chat-panel [data-radix-scroll-area-viewport] {
            padding-left: max(16px, env(safe-area-inset-left));
            padding-right: max(16px, env(safe-area-inset-right));
            padding-top: 16px;
            padding-bottom: 16px;
            scrollbar-gutter: stable both-edges;
          }

          /* Radix ScrollArea puts a wrapper div inside the viewport with min-width:100%
             and display:table. display:table sizes to CONTENT, so a wide child pushes it
             past 100% — and the max-w-[85%] message bubbles then compute against that
             inflated width instead of the panel's. Measured 2026-08-22 at the 680px
             default: scrollWidth 798 vs clientWidth 647, 31 of 42 bubbles spilling past
             the panel edge (worst 107px) with text visibly clipped.

             Forcing display:block with min-width:0 makes the wrapper honour the viewport
             width. A wide TABLE still scrolls inside its own overflow-x:auto container,
             which is intended and unaffected by this rule. */
          .neo-chat-panel [data-radix-scroll-area-viewport] > div {
            display: block !important;
            min-width: 0 !important;
            width: 100%;
          }
          
          /* Hide empty nodes in the nav actions cluster */
          #nav-actions > *:empty {
            display: none !important;
            background: transparent !important;
            border: 0 !important;
            box-shadow: none !important;
          }
        `}</style>

        <AnimatePresence>
          {capabilities.showCore && showFirstTimeSetup && (
            <FirstTimeUserSetup onComplete={handleFirstTimeSetupComplete} />
          )}
          {capabilities.showCore && showOnboarding && (
            <OnboardingTour
              userId={user?.id}
              userEmail={user?.email}
              onComplete={handleOnboardingComplete}
              onQuickAction={handleQuickAction}
            />
          )}
        </AnimatePresence>

        <nav className="bg-[#0A1A2F] sticky top-0 z-40">
          <div className="max-w-7xl mx-auto px-12 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div className="flex items-center">
                <Link to={logoHref} className="flex-shrink-0">
                   <img src={NEOGTM_LOGO_PNG_URL} alt="NEO GTM" className="h-6"/>
                </Link>

                <div className="hidden md:flex items-center space-x-2 ml-8">
                  {navigationItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.href || (item.href.includes(currentPageName) && currentPageName);

                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                          isActive
                            ? 'bg-gray-800 text-white'
                            : 'text-gray-300 hover:text-white hover:bg-gray-800/50'
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        <span>{item.name}</span>
                      </Link>
                    );
                  })}
                </div>
              </div>

              <div id="nav-actions" className="flex items-center gap-2 [&>*]:bg-transparent [&>*]:shadow-none [&>*]:border-none [&>*]:ring-0 [&>*]:outline-none">
                {user?.role === 'admin' && (
                  <TenantSwitcher 
                    className="hidden md:flex"
                    currentUser={user}
                    onTenantChange={handleAdminTenantChange}
                  />
                )}

                {capabilities.showCore && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-gray-400 hover:text-white hover:bg-gray-800/50"
                    onClick={() => window.location.href = createPageUrl('Outbox')}
                    title="Outbox"
                  >
                    <Mail className="w-5 h-5" />
                  </Button>
                )}

                <AlertBellTrigger />

                {user && (
                  <div className="hidden md:block">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="p-2 hover:bg-gray-800/50 rounded-full">
                          <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center text-sm font-bold text-gray-300 overflow-hidden">
                            {user.profile_photo_url ? (
                              <img src={user.profile_photo_url} alt="You" className="w-full h-full object-cover" />
                            ) : (
                              <span>{user.display_name ? user.display_name[0].toUpperCase() : user.email?.[0].toUpperCase()}</span>
                            )}
                          </div>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-56">
                        {capabilities.showCore && (
                          <>
                            <DropdownMenuItem asChild>
                              <Link to={createPageUrl('Analytics')} className="flex items-center gap-2">
                                <BarChart3 className="w-4 h-4" />
                                <span>{t('analytics')}</span>
                              </Link>
                            </DropdownMenuItem>
                            <DropdownMenuItem asChild>
                              <Link to={createPageUrl('Segments')} className="flex items-center gap-2">
                                <Layers className="w-4 h-4" />
                                <span>{t('segments')}</span>
                              </Link>
                            </DropdownMenuItem>
                          </>
                        )}
                        {!capabilities.showCore && capabilities.showProcure && (
                          <DropdownMenuItem asChild>
                            <Link to={createPageUrl('AlertSettings')} className="flex items-center gap-2">
                              <Bell className="w-4 h-4" />
                              <span>{t('alertSettings') || 'Alert Settings'}</span>
                            </Link>
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem asChild>
                          <Link to={createPageUrl('Settings')} className="flex items-center gap-2">
                            <Settings className="w-4 h-4" />
                            <span>{t('settings')}</span>
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to={createPageUrl('Help')} className="flex items-center gap-2">
                            <HelpCircle className="w-4 h-4" />
                            <span>{t('help')}</span>
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={handleLogout} className="text-red-600 focus:text-red-600">
                          <LogOut className="w-4 h-4 mr-2" />
                          <span>{t('logout')}</span>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                )}

                <Button
                  variant="ghost"
                  size="icon"
                  className="md:hidden text-gray-400 hover:text-white hover:bg-gray-800/50"
                  onClick={() => setShowMobileMenu(!showMobileMenu)}
                >
                  {showMobileMenu ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                </Button>
              </div>
            </div>

            <AnimatePresence>
              {showMobileMenu && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="md:hidden bg-[#0A1A2F] overflow-y-auto max-h-[calc(100vh-4rem)]"
                >
                  <div className="py-2 space-y-0.5">
                    {navigationItems.map((item) => {
                      const Icon = item.icon;
                      const isActive = location.pathname === item.href;

                      return (
                        <Link
                          key={item.name}
                          to={item.href}
                          onClick={() => setShowMobileMenu(false)}
                          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium ${
                            isActive
                              ? 'bg-gray-800 text-white'
                              : 'text-gray-300 hover:bg-gray-800/50 hover:text-white'
                          }`}
                        >
                          <Icon className="w-4 h-4" />
                          <span>{item.name}</span>
                        </Link>
                      );
                    })}
                    <DropdownMenuSeparator className="my-1 bg-gray-700" />
                    {user?.role === 'admin' && (
                      <div className="px-4 py-2">
                        <TenantSwitcher 
                          currentUser={user}
                          onTenantChange={(newTenantId) => {
                            handleAdminTenantChange(newTenantId);
                            setShowMobileMenu(false);
                          }} 
                        />
                      </div>
                    )}
                    {capabilities.showCore && (
                      <>
                        <Link
                          to={createPageUrl('Analytics')}
                          onClick={() => setShowMobileMenu(false)}
                          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-300 hover:bg-gray-800/50 hover:text-white"
                        >
                          <BarChart3 className="w-4 h-4" />
                          <span>{t('analytics')}</span>
                        </Link>
                        <Link
                          to={createPageUrl('Segments')}
                          onClick={() => setShowMobileMenu(false)}
                          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-300 hover:bg-gray-800/50 hover:text-white"
                        >
                          <Layers className="w-4 h-4" />
                          <span>{t('segments')}</span>
                        </Link>
                      </>
                    )}
                    {!capabilities.showCore && capabilities.showProcure && (
                      <Link
                        to={createPageUrl('AlertSettings')}
                        onClick={() => setShowMobileMenu(false)}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-300 hover:bg-gray-800/50 hover:text-white"
                      >
                        <Bell className="w-4 h-4" />
                        <span>{t('alertSettings') || 'Alert Settings'}</span>
                      </Link>
                    )}
                    <Link
                      to={createPageUrl('Settings')}
                      onClick={() => setShowMobileMenu(false)}
                      className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-300 hover:bg-gray-800/50 hover:text-white"
                    >
                      <Settings className="w-4 h-4" />
                      <span>{t('settings')}</span>
                    </Link>
                    <Link
                      to={createPageUrl('Help')}
                      onClick={() => setShowMobileMenu(false)}
                      className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-300 hover:bg-gray-800/50 hover:text-white"
                    >
                      <HelpCircle className="w-4 h-4" />
                      <span>{t('help')}</span>
                    </Link>
                    <button
                      onClick={() => { setShowMobileMenu(false); handleLogout(); }}
                      className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-400 hover:bg-gray-800/50 hover:text-red-300 w-full text-left"
                    >
                      <LogOut className="w-4 h-4" />
                      <span>{t('logout')}</span>
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </nav>



        <main className="max-w-7xl mx-auto px-12 sm:px-6 lg:px-8 py-8">
          <ProductRouteGuard capabilities={capabilities} isLoading={isLoading}>
            {childrenWithProps}
          </ProductRouteGuard>
        </main>

        {capabilities.showCore && (
        <motion.div
          className="hidden md:block fixed bottom-6 right-6 z-50"
          data-onboarding="neo-chat"
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ delay: 1, duration: 0.6, type: "spring", stiffness: 200 }}
        >
          <motion.button
            onClick={toggleChat}
            className="relative w-16 h-16 bg-gradient-to-r from-[#00C2C2] to-[#3FD1FF] rounded-full shadow-lg flex items-center justify-center text-white hover:shadow-xl transition-shadow"
            animate={{
              scale: [1, 1.05, 1],
              boxShadow: [
                "0 10px 25px rgba(0, 194, 194, 0.3)",
                "0 15px 35px rgba(0, 194, 194, 0.4)",
                "0 10px 25px rgba(0, 194, 194, 0.3)"
              ]
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
          >
            <MessageCircle className="w-8 h-8" />
            {briefBadge && (
              <span
                className="absolute top-2 right-2 w-3 h-3 rounded-full bg-red-500 border-2 border-white"
                aria-hidden
              />
            )}
          </motion.button>
        </motion.div>
        )}

        {capabilities.showCore && (
        <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-[#00C2C2] border-t border-[#00A3A3] safe-bottom">
          <button
            onClick={toggleChat}
            className="relative w-full flex items-center justify-center gap-2 py-3 px-4 text-white hover:bg-[#00A3A3] transition-colors"
          >
            <MessageCircle className="w-5 h-5" />
            <span className="font-medium">Chat with NEO</span>
            {briefBadge && (
              <span
                className="absolute top-2 right-3 w-2.5 h-2.5 rounded-full bg-red-500 border border-white"
                aria-hidden
              />
            )}
          </button>
        </div>
        )}

        <AnimatePresence>
          {capabilities.showCore && showChat && (
            <NeoChatPanel
              onClose={() => {
                setShowChat(false);
                setChatContext(null);
              }}
              currentUser={user}
              currentTenant={activeTenant}
              onBriefViewed={() => setBriefBadge(false)}
              context={chatContext}
              pageAccount={pageAccount}
            />
          )}
        </AnimatePresence>

        <Toaster
          position="bottom-right"
          closeButton
          richColors
          duration={6000}
          toastOptions={{
            classNames: {
              toast: 'pointer-events-auto',
              closeButton: 'pointer-events-auto cursor-pointer hover:bg-gray-100'
            }
          }}
        />
          </div>
          </ChatSessionContext.Provider>
        </CurrentTenantContext.Provider>
        </SessionTenantContext.Provider>
      </UserContext.Provider>
    </TooltipProvider>
  );
}